import styled, { keyframes } from "styled-components";
import { COLORS, THEME_FALLBACKS, INDIGO_CSS_VARS } from "../../common/colors";

// ─── Keyframes ─────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

export const kSlideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to   { opacity: 1; transform: translateX(0);     }
`;

export const kScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1);    }
`;

export const kFloat = keyframes`
  0%, 100% { transform: translateY(0px);  }
  50%       { transform: translateY(-8px); }
`;

export const kPulseRing = keyframes`
  0%   { transform: scale(0.95); box-shadow: 0 0 0 0   ${COLORS.primaryGlow}; }
  70%  { transform: scale(1);    box-shadow: 0 0 0 12px ${COLORS.primaryGlow}; }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0   ${COLORS.primaryGlow}; }
`;

export const kShimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

export const kTickerMove = keyframes`
  0%   { transform: translateX(0);    }
  100% { transform: translateX(-50%); }
`;

// ─── Section ───────────────────────────────────────────────────────────────────

export const Section = styled.section`
  /* Centralised palette tokens */
  ${INDIGO_CSS_VARS}

  /* Runtime theme overrides */
  --c-accent: ${({ theme }) => theme.buttonColor    ?? THEME_FALLBACKS.buttonColor};
  --c-text:   ${({ theme }) => theme.TextColorBlack ?? THEME_FALLBACKS.TextColorBlack};
  --c-bg:     ${({ theme }) => `rgba(${theme.body},1)` ?? COLORS.pageBg};

  --r:    1rem;
  --r-lg: 1.6rem;
  --r-xl: 2.4rem;

  --s-sm: 0 2px 14px rgba(0,0,0,0.07);
  --s:    0 4px 28px rgba(0,0,0,0.10);
  --s-lg: 0 8px 48px rgba(0,0,0,0.14);
  --s-xl: 0 16px 64px rgba(0,0,0,0.18);

  --t:      0.22s ease;
  --t-slow: 0.40s ease;

  position: relative;
  width: 100%;
  background-color: var(--c-bg);
  overflow: hidden;
  padding-bottom: 5rem;
`;
Section.displayName = "Section";

/* Decorative background blobs */
export const BgBlob = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;

  &.b1 {
    width: 600px; height: 600px;
    top: -200px; left: -180px;
    background: radial-gradient(circle, ${COLORS.primaryDark} 0%, transparent 68%);
    opacity: 0.06; filter: blur(70px);
  }
  &.b2 {
    width: 400px; height: 400px;
    bottom: -100px; right: -120px;
    background: radial-gradient(circle, ${COLORS.accentDark} 0%, transparent 68%);
    opacity: 0.07; filter: blur(60px);
  }
  &.b3 {
    width: 280px; height: 280px;
    top: 40%; right: 38%;
    background: radial-gradient(circle, ${COLORS.primaryLight} 0%, transparent 68%);
    opacity: 0.05; filter: blur(50px);
  }
  @media (max-width: 48em) {
    &.b1 { width: 300px; height: 300px; }
    &.b2 { width: 220px; height: 220px; }
    &.b3 { display: none; }
  }
`;
BgBlob.displayName = "BgBlob";

/* Decorative dotted grid overlay */
export const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: radial-gradient(circle, ${COLORS.primaryTint} 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.5;
`;
DotGrid.displayName = "DotGrid";

// ─── Inner wrapper ────────────────────────────────────────────────────────────

export const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 48em) { padding: 0 2rem; }
  @media (min-width: 64em) { padding: 0 3rem; }
`;
Inner.displayName = "Inner";

// ─── Header ───────────────────────────────────────────────────────────────────

export const HeaderWrap = styled.div`
  text-align: center;
  padding-top: 4rem;
  padding-bottom: 3rem;

  /* gradient accent bar below title */
  &::after {
    content: "";
    display: block;
    width: 56px;
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(90deg, #3d6b4f 0%, #7a9e4e 50%, #c9a84c 100%);
    margin: 1rem auto 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.7s ease both;
  }
  @media (min-width: 48em) { padding-top: 5rem; padding-bottom: 3.5rem; }
`;
HeaderWrap.displayName = "HeaderWrap";

export const Eyebrow = styled.span`
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #3d6b4f;
  margin-bottom: 0.75rem;
`;
Eyebrow.displayName = "Eyebrow";

export const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5.5vw, 3.2rem);
  font-weight: 800;
  color: #2e1f0f;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0;
`;
SectionTitle.displayName = "SectionTitle";

export const SectionDesc = styled.p`
  font-size: clamp(0.85rem, 2.2vw, 0.98rem);
  color: var(--c-text);
  max-width: 660px;
  margin: 1.5rem auto 0;
  line-height: 1.88;
  opacity: 0.7;
`;
SectionDesc.displayName = "SectionDesc";

/* Decorative divider — kept for JSX compatibility but hidden (HeaderWrap::after replaces it) */
export const Divider = styled.div`
  display: none;
`;
Divider.displayName = "Divider";

// ─── Trust bar (aggregate stats strip) ───────────────────────────────────────

export const TrustBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem 2.5rem;
  margin-bottom: 3.5rem;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.6s ease both 0.2s;
  }
`;
TrustBar.displayName = "TrustBar";

export const TrustStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
`;
TrustStat.displayName = "TrustStat";

export const TrustNumber = styled.span`
  font-size: clamp(1.4rem, 3.5vw, 1.9rem);
  font-weight: 900;
  color: var(--c-accent);
  letter-spacing: -0.03em;
  line-height: 1;
`;
TrustNumber.displayName = "TrustNumber";

export const TrustLabel = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--c-text);
  opacity: 0.5;
`;
TrustLabel.displayName = "TrustLabel";

export const TrustDivider = styled.div`
  width: 1px;
  height: 36px;
  background: var(--c-glow);

  @media (max-width: 30em) { display: none; }
`;
TrustDivider.displayName = "TrustDivider";

// ─── Auto-scroll carousel ─────────────────────────────────────────────────────

/* Outer viewport — clips horizontal overflow while allowing avatar overflow on top */
export const CarouselViewport = styled.div`
  position: relative;
  width: 100%;
  /* top padding = avatar overhang so clip doesn't cut it */
  padding-top: 3.5rem;
  margin-top: -3.5rem;
  overflow: hidden;

  /* fade-out left/right edges */
  -webkit-mask-image: linear-gradient(
    to right, transparent 0%, black 4%, black 96%, transparent 100%
  );
  mask-image: linear-gradient(
    to right, transparent 0%, black 4%, black 96%, transparent 100%
  );
`;
CarouselViewport.displayName = "CarouselViewport";

/* The scrollable track — JS drives scrollLeft for auto-scroll */
export const CardsTrack = styled.div`
  display: flex;
  gap: 1.4rem;
  /* overflow visible so avatars aren't clipped; CarouselViewport handles containment */
  overflow-x: auto;
  overflow-y: visible;
  padding: 0.5rem 1.5rem 2rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  cursor: grab;
  user-select: none;
  &::-webkit-scrollbar { display: none; }
  &:active { cursor: grabbing; }
`;
CardsTrack.displayName = "CardsTrack";

/* Alias so existing JSX (CardsGrid) keeps working without changes */
export const CardsGrid = CardsTrack;

// ─── Card ─────────────────────────────────────────────────────────────────────

export const Card = styled.article`
  flex: 0 0 290px;

  @media (min-width: 48em) { flex: 0 0 310px; }
  @media (min-width: 64em) { flex: 0 0 330px; }

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--c-surface);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid var(--c-border);
  border-radius: var(--r-xl);
  /* top padding = room for the floating avatar that hangs above */
  padding: 4.5rem 1.6rem 1.8rem;
  box-shadow: var(--s);
  cursor: default;
  transition: transform var(--t-slow), box-shadow var(--t-slow), border-color var(--t);
  text-align: center;
  /* push card down so avatar ring shows above card top edge */
  margin-top: 3rem;

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--s-xl);
    border-color: ${COLORS.borderGreen20};
  }
  &:active { transform: translateY(-2px); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 4px; }
`;
Card.displayName = "Card";

/* Scroll hint — shown only on mobile/tablet */
export const ScrollHint = styled.p`
  text-align: center;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--c-text);
  opacity: 0.32;
  margin: 0 0 3rem;

  @media (min-width: 80em) { display: none; }
`;
ScrollHint.displayName = "ScrollHint";

/* Verified badge — top-left corner */
export const VerifiedChip = styled.span`
  position: absolute;
  top: 1rem;
  left: 1.2rem;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--c-accent);
  background: var(--c-tint);
  border: 1px solid var(--c-glow);
  border-radius: 999px;
  padding: 0.18rem 0.6rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
`;
VerifiedChip.displayName = "VerifiedChip";

/* Decorative large quote mark */
export const QuoteMark = styled.span`
  position: absolute;
  top: 1rem;
  right: 1.2rem;
  font-size: 3rem;
  font-family: Georgia, serif;
  line-height: 1;
  color: var(--c-accent);
  opacity: 0.12;
  pointer-events: none;
  user-select: none;
`;
QuoteMark.displayName = "QuoteMark";

// ─── Avatar ───────────────────────────────────────────────────────────────────

export const AvatarRing = styled.div`
  position: absolute;
  top: -3.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--c-accent), ${COLORS.primaryDark});
  padding: 3px;
  box-shadow: 0 6px 24px ${COLORS.primaryGlow};

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFloat} 4s ease-in-out infinite;
    animation-delay: ${({ $index }) => $index * 0.7}s;
  }
`;
AvatarRing.displayName = "AvatarRing";

export const AvatarInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${COLORS.pureWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
AvatarInner.displayName = "AvatarInner";

export const AvatarImg = styled.img`
  width: 58px;
  height: 58px;
  object-fit: cover;
  display: block;
`;
AvatarImg.displayName = "AvatarImg";

export const AvatarFallback = styled.div`
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--c-accent);
  background: var(--c-tint);
  border-radius: 50%;
  letter-spacing: -0.03em;
`;
AvatarFallback.displayName = "AvatarFallback";

// ─── Card content ─────────────────────────────────────────────────────────────

export const ClientName = styled.h3`
  font-size: 1rem;
  font-weight: 800;
  color: var(--c-accent);
  margin: 0 0 0.2rem;
  line-height: 1.25;
`;
ClientName.displayName = "ClientName";

export const ClientRole = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--c-text);
  opacity: 0.45;
  margin: 0 0 1.1rem;
`;
ClientRole.displayName = "ClientRole";

export const CardText = styled.p`
  font-size: 0.87rem;
  color: var(--c-text);
  line-height: 1.78;
  opacity: 0.75;
  margin: 0 0 1rem;
  flex: 1;
  font-style: italic;
`;
CardText.displayName = "CardText";

/* Expand / collapse toggle */
export const ExpandBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--c-accent);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  margin-bottom: 1.2rem;
  transition: opacity var(--t);

  &:hover { opacity: 0.7; }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 3px; border-radius: 2px; }
`;
ExpandBtn.displayName = "ExpandBtn";

/* Star rating row */
export const Stars = styled.div`
  display: flex;
  gap: 0.2rem;
  margin-bottom: 1.2rem;
  justify-content: center;

  span {
    font-size: 0.9rem;
    color: ${COLORS.star};
  }
`;
Stars.displayName = "Stars";

// ─── Bottom CTA banner ────────────────────────────────────────────────────────

export const CtaBanner = styled.div`
  margin-top: 5rem;
  border-radius: var(--r-xl);
  background: linear-gradient(135deg, var(--c-accent) 0%, ${COLORS.primaryDark} 60%, ${COLORS.primaryDark} 100%);
  padding: 3rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 48px ${COLORS.primaryShadow};

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kScaleIn} 0.6s ease both 0.3s;
  }
  @media (min-width: 48em) { padding: 4rem 3rem; }
`;
CtaBanner.displayName = "CtaBanner";

export const BannerOrb = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;

  &.o1 {
    width: 300px; height: 300px;
    top: -100px; right: -80px;
    background: rgba(255,255,255,0.08);
  }
  &.o2 {
    width: 200px; height: 200px;
    bottom: -80px; left: -60px;
    background: rgba(255,255,255,0.06);
  }
`;
BannerOrb.displayName = "BannerOrb";

export const BannerTitle = styled.h3`
  font-size: clamp(1.4rem, 4vw, 2.2rem);
  font-weight: 900;
  color: ${COLORS.pureWhite};
  letter-spacing: -0.025em;
  margin: 0 0 0.8rem;
  line-height: 1.15;
`;
BannerTitle.displayName = "BannerTitle";

export const BannerDesc = styled.p`
  font-size: clamp(0.82rem, 2vw, 0.95rem);
  color: rgba(255,255,255,0.82);
  max-width: 520px;
  margin: 0 auto 2rem;
  line-height: 1.75;
`;
BannerDesc.displayName = "BannerDesc";

export const BannerStars = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 1.2rem;

  span {
    font-size: 1.1rem;
    color: ${COLORS.accent};
  }
  em {
    font-style: normal;
    font-size: 0.85rem;
    color: rgba(255,255,255,0.75);
    margin-left: 0.4rem;
  }
`;
BannerStars.displayName = "BannerStars";

export const BannerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 2.2rem;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.6);
  background: rgba(255,255,255,0.15);
  color: ${COLORS.pureWhite};
  font-size: 0.92rem;
  font-weight: 800;
  cursor: pointer;
  min-height: 50px;
  backdrop-filter: blur(10px);
  transition: background var(--t), border-color var(--t),
              transform var(--t), box-shadow var(--t);

  svg { width: 16px; height: 16px; transition: transform var(--t); }

  &:hover {
    background: rgba(255,255,255,0.28);
    border-color: rgba(255,255,255,0.9);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  }
  &:hover svg { transform: translateX(4px); }
  &:active { transform: translateY(0) scale(0.97); }
  &:focus-visible { outline: 2px solid ${COLORS.pureWhite}; outline-offset: 3px; }
`;
BannerBtn.displayName = "BannerBtn";